import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Tabs } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  getDpSoaTable
} from 'src/DigitalProtocolModule/utils/ajax-proxy'
import { useFootnoteHooks } from 'src/DigitalProtocolModule/hooks'
import styles from './index.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { clearDPFootnoteSoftSave, updateDPSoaActivities } from 'src/DigitalProtocolModule/actions/createDP'
import { ConfirmModal, Loading, ScheduleActivitiesTable, ViewFootnotes  } from "src/DigitalProtocolModule/components";
import { fetchHistory } from "src/actions/historyTrial";
import { compareArrays, deepEqual } from "src/utils/common-function";


const { TabPane } = Tabs;

interface SoaDetailsState {
  trial_id: string;
  user_name: string;
  tableItems: Digital_Protocol_SoA.LandingInfoType;
  unit: string;
}

const initialSoa = {
  soaAddedLabs: [],
  soaAddedExamination: [],
  soaAddedProcedures: [],
  soaAddedQuestionnaires: [],
  soaAddedStudyProcedures: [],
  soaVisits: [],
  soaWeeks: [],
  soaDays: [],
  soaPeriod_data: [],
  scenarios: [],
}

export default function SoaDetails() {
  const dispatch = useDispatch();
  const location = useLocation();
  const state = location.state as SoaDetailsState;
  const {
    trial_id,
    user_name,
    tableItems: { dprot_id, table_id, table_name, cohorts },
    unit
  } = state;
  const history = useHistory();

  const dpSoaState = useSelector((state: any) => state?.dpSoaReducer);
  // get footnote soft saved data
  const footnoteSoftSaveState = useSelector(fetchHistory).data.dpFootnoteSoftSave;

  const { fetchFootnoteConfig, fetchAllFootnoteData } = useFootnoteHooks();

  const [activeSoaTabKey, setActiveSoaTabKey] = useState<string>("0");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showUnsaveConfirm, setShowUnsaveConfirm] = useState(false);

  const soaUnsaveCheck = () => {
    let hasUnsaved = false
    if (dpSoaState?.scenarios.length > 0) {
      const scenario = dpSoaState?.scenarios[0]['Schedule of Activities']
      const scenario_day_window = scenario.period_data.reduce((accumulator, currentValue) => [...accumulator, ...currentValue.day_visit_window], [])
      const scenario_week_window = scenario.period_data.reduce((accumulator, currentValue) => [...accumulator, ...currentValue.week_visit_window], [])

      // Utility function to extract modality information from different sections  
      const extractSectionModalities = (section) => {  
        return section.flatMap(item => item.condition.map(cond => ({  
          visits: cond.visits,  
          modality: cond.modality  
        })));  
      };  
    
      // Extracting modalities for each section  
      const currentLabModalities = extractSectionModalities(dpSoaState.soaAddedLabs);  
      const scenarioLabModalities = extractSectionModalities(scenario["Labs"].entities);  
    
      const currentExaminationModalities = extractSectionModalities(dpSoaState.soaAddedExamination);  
      const scenarioExaminationModalities = extractSectionModalities(scenario["Physical Examination"].entities);  
    
      const currentProcedureModalities = extractSectionModalities(dpSoaState.soaAddedProcedures);  
      const scenarioProcedureModalities = extractSectionModalities(scenario["Procedures"].entities);  
    
      const currentQuestionnaireModalities = extractSectionModalities(dpSoaState.soaAddedQuestionnaires);  
      const scenarioQuestionnaireModalities = extractSectionModalities(scenario["Questionnaires"].entities);  
    
      const currentStudyProcedureModalities = extractSectionModalities(dpSoaState.soaAddedStudyProcedures);  
      const scenarioStudyProcedureModalities = extractSectionModalities(scenario["Study Procedures"].entities);  

      hasUnsaved = hasUnsaved
        || !compareArrays(dpSoaState.soaAddedExamination.map(item => item.activity_id), scenario["Physical Examination"].entities.map(item => item.activity_id))
        || !compareArrays(dpSoaState.soaAddedLabs.map(item => item.activity_id), scenario["Labs"].entities.map(item => item.activity_id))
        || !compareArrays(dpSoaState.soaAddedProcedures.map(item => item.activity_id), scenario["Procedures"].entities.map(item => item.activity_id))
        || !compareArrays(dpSoaState.soaAddedQuestionnaires.map(item => item.activity_id), scenario["Questionnaires"].entities.map(item => item.activity_id))
        || !compareArrays(dpSoaState.soaAddedStudyProcedures.map(item => item.activity_id), scenario["Study Procedures"].entities.map(item => item.activity_id))
        || !compareArrays(dpSoaState.soaVisits, scenario["visits"])
        || !compareArrays(dpSoaState.soaPeriod_data.map(item => item.period_id), scenario["period_data"].map(item => item.period_id))
        || !deepEqual(dpSoaState.soaDays, scenario["Days"]) 
        || !deepEqual(dpSoaState.soaWeeks, scenario["Weeks"])
        || !deepEqual(dpSoaState.soaDayWindow, scenario_day_window)  
        || !deepEqual(dpSoaState.soaWeekWindow, scenario_week_window) 
        || !deepEqual(currentLabModalities, scenarioLabModalities) // Modality comparison for Labs  
        || !deepEqual(currentExaminationModalities, scenarioExaminationModalities) // Modality comparison for Physical Examination  
        || !deepEqual(currentProcedureModalities, scenarioProcedureModalities) // Modality comparison for Procedures  
        || !deepEqual(currentQuestionnaireModalities, scenarioQuestionnaireModalities) // Modality comparison for Questionnaires  
        || !deepEqual(currentStudyProcedureModalities, scenarioStudyProcedureModalities); // Modality comparison for Study Procedures  
    }
    return hasUnsaved
  }

  const footnoteUnsaveCheck = () => {
    return footnoteSoftSaveState?.delete?.length > 0 || footnoteSoftSaveState?.insert?.length > 0 || footnoteSoftSaveState?.update?.length > 0
  }

  const unsavedDataCheck = () => {
    if (soaUnsaveCheck() || footnoteUnsaveCheck()) {
      setShowUnsaveConfirm(true)
    } else {
      backToTrial()
    }
  }

  const backToTrial = () => {
    history.replace("/dprotocol/soa");
    dispatch(updateDPSoaActivities(initialSoa));
    // reset soft saved footnote data
    dispatch(clearDPFootnoteSoftSave());
  };

  const fetchSoAData = async () => {
    try {
      setIsLoading(true);
      const res = await getDpSoaTable(dprot_id, trial_id, user_name);
      dispatch(updateDPSoaActivities(res));
      return res;
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // get footnote types and other informations
  const fetchFootnoteInfo = (soaTableRes: Digital_Protocol_SoA_Table_Data.DP_Scenario) => {
    // get footnote type list and dropdown options to redux
    fetchFootnoteConfig(trial_id);
    // get footnote data to redux
    fetchAllFootnoteData(trial_id, table_id, unit, soaTableRes, setIsLoading);
  }
  
  useEffect(() => {
    fetchSoAData().then((soaTableRes) => {
      fetchFootnoteInfo(soaTableRes);
    });
  }, []);

  return (
    <>
      <div className={styles.header}>
        <div className="header-back" onClick={unsavedDataCheck}>
          <ArrowLeftOutlined className="arrow-icon" />
          <div className={styles.backBtn}>Back to all</div>
        </div>

        <div className={styles.headerTableInfo}>
          <span className={styles.tableName}> {table_name} </span>
          {cohorts &&
            cohorts.map((v) => (
              <span className={styles.cohortInfo}> {v?.cohort_name} </span>
            ))}
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div
          className={`${styles.activeTabName} ${
            activeSoaTabKey === "0" ? styles.active : ""
          }`}
          onClick={() => setActiveSoaTabKey("0")}
        >
          <span className="tab-Name">Schedule of activities</span>
        </div>
        <div
          className={`${styles.activeTabName} ${
            activeSoaTabKey === "1" ? styles.active : ""
          }`}
          onClick={() => setActiveSoaTabKey("1")}
        >
          <span className="tab-Name">Footnotes</span>
        </div>
      </div>

      <Loading isLoading={isLoading}>
        <div className={styles.bodyWrapper}>
          <Tabs centered activeKey={activeSoaTabKey}>
            <TabPane key="0">
              {activeSoaTabKey === "0" && <ScheduleActivitiesTable fetchSoAData={fetchSoAData}/>}
            </TabPane>
            <TabPane key="1">
              {activeSoaTabKey === "1" && <ViewFootnotes />}
            </TabPane>
          </Tabs>
        </div>
      </Loading>

      <ConfirmModal
        visible={showUnsaveConfirm}
        title={'Leave Page'}
        content={
          'You have unsaved changes, are you sure you want to leave this page?'
        }
        handleCancel={() => setShowUnsaveConfirm(false)}
        handleConfirm={() => {
          backToTrial()
          setShowUnsaveConfirm(false)
        }}
      />
    </>
  );
}
